import React, { useEffect, useState } from "react";
import { get, post, put, remove } from "../../redux/ApiHelper";
import { errorToast, successToast } from "../../utils/toast.util";
import { QuestionPaperInterface } from "../../interfaces/question-paper.interface";
import { QuestionInterface } from "../../interfaces/question.interface";
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Add, Edit, Delete, Publish, RemoveCircle } from "@mui/icons-material";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import { QuestionPaperFormDialog, QuestionPaperViewDialog } from "../../components/examination/QuestionPaperDialogs";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";

const CoinEarner = () => {
  const navigate = useNavigate();
  const [questionPapers, setQuestionPapers] = useState<QuestionPaperInterface[]>([]);
  const [filteredPapers, setFilteredPapers] = useState<QuestionPaperInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingPaper, setEditingPaper] = useState<QuestionPaperInterface | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [availableQuestions, setAvailableQuestions] = useState<QuestionInterface[]>([]);
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  // Form state
  const [formData, setFormData] = useState<Partial<QuestionPaperInterface>>({
    title: "",
    description: "",
    subject: "",
    class: "",
    examType: "coin earner",
    examStartDateTime: "",
    durationInMinutes: 0,
    progressLevel: 1,
    fullMarks: 0,
    passMarks: 0,
    perticiationChargeInCoins: 0,
    resultDate: "",
    isResultPublished: false,
    questions: [],
    prizePool: {
      rankRewards: [
        { rank: 1, coins: 100 },
        { rank: 2, coins: 50 },
        { rank: 3, coins: 20 },
      ],
      defaultCoins: 0,
      applyToAll: false,
    },
  });

  // Validation errors
  const [errors, setErrors] = useState<{
    title?: string;
    subject?: string;
    class?: string;
    perticiationChargeInCoins?: string;
    resultDate?: string;
    examStartDateTime?: string;
    durationInMinutes?: string;
    fullMarks?: string;
    passMarks?: string;
    questions?: string;
  }>({});

  const [publishReviewOpen, setPublishReviewOpen] = useState(false);
  const [publishReviewPaper, setPublishReviewPaper] = useState<QuestionPaperInterface | null>(null);
  const [publishReviewLoading, setPublishReviewLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchQuestionPapers();
    fetchSubjects();
  }, []);

  useEffect(() => {
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = questionPapers.filter(
      (p) =>
        p.title?.toLowerCase().includes(lowerQuery) ||
        p.subject?.toLowerCase().includes(lowerQuery)
    );
    setFilteredPapers(filtered);
  }, [questionPapers, searchQuery]);

  useEffect(() => {
    if (formData.subject) {
      fetchQuestionsBySubject(formData.subject);
    }
  }, [formData.subject]);

  const fetchQuestionPapers = async () => {
    try {
      setLoading(true);
      const response = await get("/admin/examination/question-papers", {
        examType: "coin earner",
      });
      setQuestionPapers(response.data || []);
      setFilteredPapers(response.data || []);
    } catch (error) {
      console.error("Error fetching question papers:", error);
      errorToast("Failed to fetch question papers");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await get("/admin/subject/get-all");
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchQuestionsBySubject = async (subject: string) => {
    try {
      const response = await get("/admin/examination/questions", { subject });
      setAvailableQuestions(response.data || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };


  const handleOpenModal = async (paper?: QuestionPaperInterface) => {
    if (paper?._id) {
      try {
        setLoading(true);
        const detailedResp = await get(`/admin/examination/question-paper/${paper._id}`);
        const detailed = (detailedResp?.data || paper) as any;
        setEditingPaper(detailed);
        setFormData({
          title: detailed.title,
          description: detailed.description || "",
          subject: detailed.subject,
          class: detailed.class || "",
          examType: "coin earner",
          examStartDateTime: detailed.examStartDateTime
            ? new Date(detailed.examStartDateTime).toISOString().slice(0, 16)
            : "",
          durationInMinutes: detailed.durationInMinutes,
          progressLevel: Number(detailed.progressLevel) || 1,
          fullMarks: detailed.fullMarks,
          passMarks: detailed.passMarks,
          perticiationChargeInCoins: Number(detailed.perticiationChargeInCoins) || 0,
          resultDate: detailed.resultDate
            ? new Date(detailed.resultDate).toISOString().slice(0, 16)
            : "",
          isResultPublished: Boolean(detailed.isResultPublished),
          questions: Array.isArray(detailed.questions)
            ? detailed.questions.map((q: any) => (typeof q === "string" ? q : q))
            : [],
          prizePool: detailed.prizePool || {
            rankRewards: [
              { rank: 1, coins: 100 },
              { rank: 2, coins: 50 },
              { rank: 3, coins: 20 },
            ],
            defaultCoins: 0,
            applyToAll: false,
          },
        });
        setOpenModal(true);
      } catch (error) {
        console.error("Error fetching question paper details:", error);
        errorToast("Failed to fetch question paper details");
      } finally {
        setLoading(false);
      }
      return;
    }

    setEditingPaper(null);
    setFormData({
      title: "",
      description: "",
      subject: "",
      class: "",
      examType: "coin earner",
      examStartDateTime: "",
      durationInMinutes: 0,
      progressLevel: 1,
      fullMarks: 0,
      passMarks: 0,
      perticiationChargeInCoins: 0,
      resultDate: "",
      isResultPublished: false,
      questions: [],
      prizePool: {
        rankRewards: [
          { rank: 1, coins: 100 },
          { rank: 2, coins: 50 },
          { rank: 3, coins: 20 },
        ],
        defaultCoins: 0,
        applyToAll: false,
      },
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPaper(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.title || formData.title.trim() === "") {
      newErrors.title = "Title is required";
    }

    if (!formData.subject || formData.subject.trim() === "") {
      newErrors.subject = "Subject is required";
    }

    if (!formData.class || String(formData.class).trim() === "") {
      newErrors.class = "Class is required";
    }

    const charge = Number((formData as any).perticiationChargeInCoins);
    if (!Number.isFinite(charge) || charge < 0) {
      newErrors.perticiationChargeInCoins = "Participation charge must be 0 or more";
    }

    if ((formData as any).resultDate) {
      const d = new Date((formData as any).resultDate);
      if (Number.isNaN(d.getTime())) {
        newErrors.resultDate = "Result date is invalid";
      }
    }

    if (!formData.examStartDateTime) {
      newErrors.examStartDateTime = "Exam start date and time is required";
    } else {
      const examDate = new Date(formData.examStartDateTime);
      const now = new Date();
      if (examDate < now) {
        newErrors.examStartDateTime = "Exam start date must be in the future";
      }
    }

    if (!formData.durationInMinutes || formData.durationInMinutes <= 0) {
      newErrors.durationInMinutes = "Duration must be greater than 0";
    }

    if (!formData.fullMarks || formData.fullMarks <= 0) {
      newErrors.fullMarks = "Full marks must be greater than 0";
    }

    if (!formData.passMarks || formData.passMarks <= 0) {
      newErrors.passMarks = "Pass marks must be greater than 0";
    } else if (formData.fullMarks && formData.passMarks > formData.fullMarks) {
      newErrors.passMarks = "Pass marks cannot be greater than full marks";
    }

    setErrors(newErrors);
    const messages = Object.values(newErrors).filter(Boolean);
    if (messages.length) {
      errorToast(messages.join(". "));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        questions: (formData.questions || [])
          .map((q: any) => (typeof q === "string" ? q : q?._id))
          .filter(Boolean),
        adminEmail: signedAdmin.email,
        adminRole: signedAdmin.role,
        adminType: signedAdmin.type,
      };

      if (editingPaper?._id) {
        await put(`/admin/examination/question-paper/${editingPaper._id}`, payload);
        successToast("Question paper updated successfully");
        handleCloseModal();
        fetchQuestionPapers();
      } else {
        await post("/admin/examination/question-paper/create", payload);
        successToast("Question paper created successfully");
        handleCloseModal();
        fetchQuestionPapers();
      }
    } catch (error) {
      console.error("Error saving question paper:", error);
      errorToast("Failed to save question paper");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (paperId: string) => {
    if (!window.confirm("Are you sure you want to delete this question paper?")) {
      return;
    }

    try {
      setLoading(true);
      await remove(`/admin/examination/question-paper/${paperId}`);
      successToast("Question paper deleted successfully");
      fetchQuestionPapers();
    } catch (error) {
      console.error("Error deleting question paper:", error);
      errorToast("Failed to delete question paper");
    } finally {
      setLoading(false);
    }
  };

  const openPublishReview = async (paperId: string) => {
    try {
      setPublishReviewLoading(true);
      const resp = await get(`/admin/examination/question-paper/${paperId}`);
      setPublishReviewPaper(resp?.data || null);
      setPublishReviewOpen(true);
    } catch (error) {
      console.error("Error fetching question paper details:", error);
      errorToast("Failed to fetch question paper details");
    } finally {
      setPublishReviewLoading(false);
    }
  };

  const confirmPublish = async () => {
    const paperId = publishReviewPaper?._id;
    if (!paperId) return;

    try {
      setPublishing(true);
      await put(`/admin/examination/question-paper/${paperId}/publish`, {
        adminEmail: signedAdmin.email,
        adminRole: signedAdmin.role,
        adminType: signedAdmin.type,
      });
      successToast("Question paper published successfully");
      setPublishReviewOpen(false);
      setPublishReviewPaper(null);
      fetchQuestionPapers();
    } catch (error) {
      console.error("Error publishing question paper:", error);
      errorToast("Failed to publish question paper");
    } finally {
      setPublishing(false);
    }
  };

  const handleToggleQuestion = (questionId: string) => {
    const currentQuestions = formData.questions || [];
    const questionIds = currentQuestions.map((q: any) => (typeof q === "string" ? q : q._id));
    const newQuestions = questionIds.includes(questionId)
      ? questionIds.filter((id) => id !== questionId)
      : [...questionIds, questionId];
    setFormData({ ...formData, questions: newQuestions });
    if (errors.questions && newQuestions.length > 0) {
      setErrors({ ...errors, questions: undefined });
    }
  };

  const handleAddRankReward = () => {
    const currentRewards = formData.prizePool?.rankRewards || [];
    const nextRank = currentRewards.length > 0
      ? Math.max(...currentRewards.map(r => r.rank)) + 1
      : 1;
    const newRewards = [...currentRewards, { rank: nextRank, coins: 0 }];
    setFormData({
      ...formData,
      prizePool: {
        ...formData.prizePool!,
        rankRewards: newRewards,
      },
    });
  };

  const handleRemoveRankReward = (index: number) => {
    const currentRewards = formData.prizePool?.rankRewards || [];
    const newRewards = currentRewards.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      prizePool: {
        ...formData.prizePool!,
        rankRewards: newRewards,
      },
    });
  };

  const handleUpdateRankReward = (index: number, field: 'rank' | 'coins', value: number) => {
    const currentRewards = formData.prizePool?.rankRewards || [];
    const newRewards = [...currentRewards];
    newRewards[index] = { ...newRewards[index], [field]: value };
    setFormData({
      ...formData,
      prizePool: {
        ...formData.prizePool!,
        rankRewards: newRewards,
      },
    });
  };

  const handleUpdateDefaultCoins = (value: number) => {
    setFormData({
      ...formData,
      prizePool: {
        ...formData.prizePool!,
        defaultCoins: value,
      },
    });
  };

  const handleToggleApplyToAll = (checked: boolean) => {
    setFormData({
      ...formData,
      prizePool: {
        ...formData.prizePool!,
        applyToAll: checked,
      },
    });
  };

  const columns: Column[] = [
    { select: "title", header: "Title", align: "left" },
    { select: "subject", header: "Subject", align: "left" },
    {
      select: "examStartDateTime",
      header: "Exam Date",
      render: (value: string | Date) =>
        new Date(value).toLocaleString(),
    },
    { select: "durationInMinutes", header: "Duration (min)" },
    { select: "fullMarks", header: "Full Marks" },
    { select: "passMarks", header: "Pass Marks" },
    {
      select: "isPublished",
      header: "Status",
      render: (value: boolean) => (
        <Chip
          label={value ? "Published" : "Draft"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      select: "actions",
      header: "Actions",
      render: (_: any, row: QuestionPaperInterface) => (
        <Box>
          {!row.isPublished && (<IconButton
            size="small"
            onClick={() => handleOpenModal(row)}
            color="primary"
          >
            <Edit />
          </IconButton>)}

          {!row.isPublished && (
            <IconButton
              size="small"
              onClick={() => openPublishReview(row._id!)}
              color="success"
            >
              <Publish />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => handleDelete(row._id!)}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" className="text-white">Coin Earner Exam Papers</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenModal()}
          sx={{
            backgroundColor: "#fcd307",
            color: "#000000",
            "&:hover": {
              backgroundColor: "#e6be06",
            },
          }}
        >
          Create Question Paper
        </Button>
      </Box>

      <TextField
        fullWidth
        label="Search Question Papers"
        variant="outlined"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            color: "#ffffff",
            "& fieldset": {
              borderColor: "#ffffff",
            },
            "&:hover fieldset": {
              borderColor: "#ffffff",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#ffffff",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#ffffff",
            "&.Mui-focused": {
              color: "#ffffff",
            },
          },
          "& .MuiInputBase-input": {
            color: "#ffffff",
          },
        }}
      />

      <TableComponent
        data={filteredPapers}
        columns={columns}
      />

      <QuestionPaperFormDialog
        open={openModal}
        mode={editingPaper ? "edit" : "create"}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        loading={loading}
        questionPaperId={editingPaper?._id}
        signedAdmin={signedAdmin}
        onPaperUpdated={(paper) => {
          if (paper?._id) setEditingPaper(paper);
          fetchQuestionPapers();
        }}
        subjects={subjects}
        availableQuestions={availableQuestions}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onToggleQuestion={handleToggleQuestion}
        onGoToQuestionsPage={() => navigate("/examination/questions")}
        extraContentBeforeQuestions={
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, color: "#fcd307", fontWeight: "bold" }}
            >
              Prize Pool Settings
            </Typography>

            <Box sx={{ mb: 2 }}>
              <TextField
                type="number"
                label="Participation charge (coins)"
                value={(formData as any).perticiationChargeInCoins ?? 0}
                onChange={(e) => {
                  const next = e.target.value === "" ? 0 : Number(e.target.value);
                  setFormData({ ...formData, perticiationChargeInCoins: next });
                  if (errors.perticiationChargeInCoins) {
                    setErrors({ ...errors, perticiationChargeInCoins: undefined });
                  }
                }}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                helperText={errors.perticiationChargeInCoins || "Coins deducted each time a student participates."}
                error={!!errors.perticiationChargeInCoins}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    "& fieldset": { borderColor: "#ffffff" },
                    "&:hover fieldset": { borderColor: "#ffffff" },
                    "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#ffffff",
                    "&.Mui-focused": { color: "#ffffff" },
                  },
                  "& .MuiInputBase-input": { color: "#ffffff" },
                  "& .MuiFormHelperText-root": { color: errors.perticiationChargeInCoins ? "#ff0000" : "#ffffff" },
                }}
              />
            </Box>

            <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                type="datetime-local"
                label="Result date (optional)"
                value={(formData as any).resultDate || ""}
                onChange={(e) => {
                  setFormData({ ...formData, resultDate: e.target.value });
                  if (errors.resultDate) setErrors({ ...errors, resultDate: undefined });
                }}
                InputLabelProps={{ shrink: true }}
                error={!!errors.resultDate}
                helperText={errors.resultDate}
                sx={{
                  flex: 1,
                  minWidth: 260,
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    "& fieldset": { borderColor: "#ffffff" },
                    "&:hover fieldset": { borderColor: "#ffffff" },
                    "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#ffffff",
                    "&.Mui-focused": { color: "#ffffff" },
                  },
                }}
              />
            </Box>

            {/* Rank Rewards */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: "#ffffff" }}>
                  Rank-Based Rewards
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddRankReward}
                  sx={{
                    color: "#fcd307",
                    borderColor: "#fcd307",
                    "&:hover": {
                      borderColor: "#e6be06",
                      backgroundColor: "rgba(252, 211, 7, 0.1)",
                    },
                  }}
                  variant="outlined"
                >
                  Add Rank
                </Button>
              </Box>

              <Box
                sx={{
                  maxHeight: 250,
                  overflow: "auto",
                  border: "1px solid #ffffff",
                  p: 1,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                {formData.prizePool?.rankRewards.map((reward: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <TextField
                      type="number"
                      label="Rank"
                      value={reward.rank}
                      onChange={(e) => handleUpdateRankReward(index, "rank", Number(e.target.value))}
                      size="small"
                      sx={{
                        width: 100,
                        "& .MuiOutlinedInput-root": {
                          color: "#ffffff",
                          "& fieldset": { borderColor: "#ffffff" },
                          "&:hover fieldset": { borderColor: "#ffffff" },
                          "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                        },
                        "& .MuiInputLabel-root": {
                          color: "#ffffff",
                          "&.Mui-focused": { color: "#ffffff" },
                        },
                        "& .MuiInputBase-input": { color: "#ffffff" },
                      }}
                    />
                    <TextField
                      type="number"
                      label="Coins"
                      value={reward.coins}
                      onChange={(e) => handleUpdateRankReward(index, "coins", Number(e.target.value))}
                      size="small"
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          color: "#ffffff",
                          "& fieldset": { borderColor: "#ffffff" },
                          "&:hover fieldset": { borderColor: "#ffffff" },
                          "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                        },
                        "& .MuiInputLabel-root": {
                          color: "#ffffff",
                          "&.Mui-focused": { color: "#ffffff" },
                        },
                        "& .MuiInputBase-input": { color: "#ffffff" },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveRankReward(index)}
                      sx={{
                        color: "#ff4444",
                        "&:hover": { backgroundColor: "rgba(255, 68, 68, 0.1)" },
                      }}
                    >
                      <RemoveCircle />
                    </IconButton>
                  </Box>
                ))}
                {(!formData.prizePool?.rankRewards || formData.prizePool.rankRewards.length === 0) && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#ffffff", textAlign: "center", py: 2 }}
                  >
                    No rank rewards configured. Click "Add Rank" to add one.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Default Coins */}
            <Box sx={{ mb: 2 }}>
              <TextField
                type="number"
                label="Default Coins (for rest/all participants)"
                value={formData.prizePool?.defaultCoins || 0}
                onChange={(e) => handleUpdateDefaultCoins(Number(e.target.value))}
                fullWidth
                helperText={
                  formData.prizePool?.applyToAll
                    ? "All participants will receive this amount"
                    : "Participants not in rank rewards will receive this amount"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    "& fieldset": { borderColor: "#ffffff" },
                    "&:hover fieldset": { borderColor: "#ffffff" },
                    "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#ffffff",
                    "&.Mui-focused": { color: "#ffffff" },
                  },
                  "& .MuiInputBase-input": { color: "#ffffff" },
                  "& .MuiFormHelperText-root": { color: "#ffffff" },
                }}
              />
            </Box>

            {/* Apply To All Toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.prizePool?.applyToAll || false}
                  onChange={(e) => handleToggleApplyToAll(e.target.checked)}
                  sx={{
                    color: "#ffffff",
                    "&.Mui-checked": { color: "#fcd307" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#ffffff" }}>
                  Apply default coins to all participants (not just those not in rank rewards)
                </Typography>
              }
            />
          </Box>
        }
      />

      <QuestionPaperViewDialog
        open={publishReviewOpen}
        onClose={() => {
          if (publishing) return;
          setPublishReviewOpen(false);
          setPublishReviewPaper(null);
        }}
        selectedPaper={publishReviewPaper}
        primaryActionText={publishReviewLoading ? "Loading..." : publishing ? "Publishing..." : "Publish"}
        primaryActionDisabled={
          publishReviewLoading ||
          publishing ||
          !publishReviewPaper?._id ||
          (((publishReviewPaper as any)?.questions?.length ?? 0) === 0)
        }
        onPrimaryAction={confirmPublish}
      />
    </Box>
  );
};

export default CoinEarner;
